import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { firebaseAuth } from '../lib/firebase';
import { apiFetch, ApiError } from '../lib/api';

/**
 * Magic-link landing page. The email-code email (POST /auth/email/start)
 * includes both the 6-digit code AND a "continue" link pointing here, so a
 * user who can't find the browser tab with the code-entry dialog can just
 * click the link to be signed in and dropped back into the registration
 * wizard (or, for a completed account, the dashboard).
 *
 * The email + code travel in the URL *fragment* (`#e=…&c=…`) — browsers
 * never send fragments to the server, so the one-time code never lands in
 * nginx/api access logs. We read it client-side and POST it to the same
 * /api/auth/email/verify endpoint the OTP form uses, then sign in with the
 * returned custom token and navigate to /register, where the wizard's
 * /me-driven resume logic picks the right next step.
 */
export default function AuthContinue() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const hash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    const params = new URLSearchParams(hash);
    const email = params.get('e');
    const code = params.get('c');
    if (!email || !code) {
      setError('invalid_link');
      return;
    }

    let alive = true;
    (async () => {
      try {
        const res = await apiFetch<{ customToken: string }>('/api/auth/email/verify', {
          method: 'POST',
          body: JSON.stringify({ email, code }),
        });
        if (res.customToken) {
          await signInWithCustomToken(firebaseAuth, res.customToken);
        }
        if (!alive) return;
        // Strip the fragment so a refresh / re-share can't replay the code,
        // then resume the wizard. decideWizardResume bounces an already-
        // completed account on to the dashboard.
        navigate('/register', { replace: true });
      } catch (err) {
        if (!alive) return;
        setError(err instanceof ApiError ? err.code : (err as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [navigate]);

  return (
    <main className="reg-wrap">
      {error ? (
        <div className="reg-step" style={{ textAlign: 'center' }}>
          <h2 className="reg-step-title">הקישור פג או אינו תקין</h2>
          <p className="reg-step-sub">
            ייתכן שהקוד פג תוקף (10 דקות) או שכבר נעשה בו שימוש. חזרו לדף ההרשמה
            ובקשו קוד חדש.
          </p>
          <button
            type="button"
            className="reg-cta"
            onClick={() => navigate('/register', { replace: true })}
          >
            חזרה להרשמה
          </button>
        </div>
      ) : (
        <p className="reg-step-sub centered">מתחברים…</p>
      )}
    </main>
  );
}
